import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ADD_ONS, type Client, type ServiceTier, type ServiceType } from '../types';

export interface SelectedService {
  id: string;
  name: string;
  monthlyFee: number;
  initialCost?: number;
  description: string;
}

interface OnboardingStore {
  currentStep: number;
  client: Partial<Client>;
  selectedServiceTypes: ServiceType[];
  selectedTier: ServiceTier | null;
  selectedServices: SelectedService[];
  selectedAddOns: string[];
  textAvailabilityNotificationsAllowed: boolean;
  setStep: (step: number) => void;
  updateClient: (data: Partial<Client>) => void;
  toggleServiceType: (serviceType: ServiceType) => void;
  setSelectedServiceTypes: (types: ServiceType[]) => void;
  setSelectedTier: (tier: ServiceTier | null) => void;
  setSelectedServices: (services: SelectedService[]) => void;
  setSelectedAddOns: (addOns: string[]) => void;
  setTextAvailabilityNotificationsAllowed: (allowed: boolean) => void;
  reset: () => void;
}

const initialState = {
  currentStep: 0,
  client: {},
  selectedServiceTypes: [],
  selectedTier: null,
  selectedServices: [],
  selectedAddOns: [],
  textAvailabilityNotificationsAllowed: false,
};

const ACTIVE_ADD_ON_IDS = new Set(ADD_ONS.filter(addOn => addOn.status === 'active').map(addOn => addOn.id));

function sanitizeSelectedAddOns(addOns: string[]) {
  if (!Array.isArray(addOns)) return [];
  return addOns.filter(addOn => ACTIVE_ADD_ON_IDS.has(addOn));
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set) => ({
      ...initialState,
      setStep: (step) => set((state) => {
        // Prevent invalid steps (7 screens total: 0-6)
        if (step < 0 || step > 6) {
          return state;
        }

        // Scroll to top of page when changing steps
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
          });
        }, 0);

        return { currentStep: step };
      }),
      updateClient: (data) => set((state) => ({ 
        client: { ...state.client, ...data }
      })),
      toggleServiceType: (serviceType) => set((state) => {
        const isSelected = state.selectedServiceTypes.some(type => type.id === serviceType.id);
        const newSelectedTypes = isSelected
          ? state.selectedServiceTypes.filter(type => type.id !== serviceType.id)
          : [...state.selectedServiceTypes, serviceType];

        // Reset tier and selected services when changing service types
        return {
          selectedServiceTypes: newSelectedTypes,
          selectedTier: null,
          selectedServices: [],
          selectedAddOns: [],
        };
      }),
      setSelectedServiceTypes: (types) => set(() => ({ selectedServiceTypes: types })),
      setSelectedTier: (tier) => set(() => ({ selectedTier: tier })),
      setSelectedServices: (services) => set(() => ({ selectedServices: services })),
      setSelectedAddOns: (addOns) => set(() => ({ selectedAddOns: sanitizeSelectedAddOns(addOns) })),
      setTextAvailabilityNotificationsAllowed: (allowed) => set(() => ({
        textAvailabilityNotificationsAllowed: allowed === true,
      })),
      reset: () => set(initialState),
    }),
    {
      name: 'onboarding-storage',
      partialize: (state) => ({
        currentStep: state.currentStep,
        client: state.client,
        selectedServiceTypes: state.selectedServiceTypes,
        selectedTier: state.selectedTier,
        selectedServices: state.selectedServices,
        selectedAddOns: state.selectedAddOns,
        textAvailabilityNotificationsAllowed: state.textAvailabilityNotificationsAllowed,
      }),
    }
  )
);
